import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import longArrayRight from '../../../public/assets/icons/longArrayRight.svg';
import { getUserData } from '../../../utils';

import { checkIsAuthenticated } from '../../../utils/authUtils';
import { RONS_TIPS_HOME_META } from '../../../utils/meta';

import {
  ACCOUNT_SETTINGS_NEWSLETTER_CITY,
  LOGIN_SIGNUP_ROUTE,
} from '../../../utils/routes';
import { useAds } from '../../contexts/AdsContext';
import { useRonsTipsUtils } from '../../contexts/RonsTipsContext';
import useMobileDetect from '../../hooks/useMobileDetect';
import { getRonTipsHomeAdsAsync } from '../../store/slices/common/adsSlice';
import { getMarketPostsAsync } from '../../store/slices/market/marketSlice';
import {
  getFollowedCategoriesAndSubcategories,
  getRonsTipsCategoryPosts,
  resetRonsTipsPosts,
} from '../../store/slices/ronstips/ronstipsSlice';
import { all_cities } from '../../../utils/cityUtils';

// Dynamic imports for components
const InfiniteScroll = dynamic(
  () => import('react-infinite-scroll-component'),
  {
    ssr: false,
  }
);
const ContainerWithAd = dynamic(
  () => import('../../components/Common/ContainerWithAd/ContainerWithAd')
);
const CustomIcon = dynamic(
  () => import('../../components/Common/CustomIcon/CustomIcon'),
  {
    loading: () => <div className='min-h-[16px] min-w-[16px]'></div>,
  }
);
const ForYouSection = dynamic(
  () => import('../../components/Common/ForYouSection/ForYouSection')
);
const SurveyCard = dynamic(
  () => import('../../components/Community/CommunityCard/SurveyCard/SurveyCard')
);
const TagSection = dynamic(
  () => import('../../components/Community/TagSection/TagSection'),
  {
    loading: () => <div className='min-h-[70px]'></div>,
  }
);
const DonationBox = dynamic(
  () => import('../../components/DonationBox/DonationBox'),
  {
    loading: () => <div className='min-h-[620px]'></div>,
  }
);
const GoogleAdBannerHorizontal = dynamic(
  () => import('../../components/GoogleAd/GoogleAdBannerHorizontal')
);
const GoogleAdBannerTop = dynamic(
  () => import('../../components/GoogleAd/GoogleAdBannerTop')
);
const GoogleAdBannerVertical2 = dynamic(
  () => import('../../components/GoogleAd/GoogleAdBannerVertical2')
);
const EventSection = dynamic(
  () => import('../../components/Market/EventSection/EventSection')
);
const EcommerceAds = dynamic(
  () => import('../../components/Market/MarketAdContainer/EcommerceAds')
);

const PageContainer = dynamic(
  () => import('../../components/PageContainer/PageContainer')
);
const RonCastsSection = dynamic(
  () => import('../../components/RonTips/RonCastsSection/RonCastsSection'),
  {
    loading: () => <div className='min-h-[500px]'></div>,
  }
);
const RonsTipsCategorySection = dynamic(
  () => import('../../components/RonTips/RonsTipsCategorySection'),
  {
    loading: () => <div className='min-h-[600px]'></div>,
  }
);
const SectionHeader = dynamic(
  () => import('../../components/RonTips/SectionHeader/SectionHeader')
);
const SubscribeSection = dynamic(
  () => import('../../components/RonTips/SubscribeSection/SubscribeSection'),
  {
    loading: () => <div className='min-h-[350px]'></div>,
  }
);
// const RonsTipsCategorySectionSkeleton = dynamic(
//   () =>
//     import('../../components/SkeletonLoaders/RonsTipsCategorySectionSkeleton')
// );

export async function getServerSideProps({ query }) {
  const { city } = query;
  return {
    props: { metaData: RONS_TIPS_HOME_META(city) },
  };
}

export default function RonTipsPage() {
  const { GOOGLE_ADS_RONS_TIPS_CHANNEL, ronsTipsHomeGoogleAdsKeyValues } =
    useAds();
  const dispatch = useDispatch();
  const loading = useSelector(({ ronstips }) => ronstips?.loading);
  const posts = useSelector(({ ronstips }) => ronstips?.ronsTipsCategoryPosts);
  const selectedCity = useSelector(({ cities }) => cities?.selectedCity);
  const userData = getUserData();
  const [skip, setSkip] = useState(0);

  function loadMore() {
    setSkip((prev) => prev + 2);
    dispatch(
      getRonsTipsCategoryPosts({ cityId: selectedCity?.id, skip: skip + 2 })
    );
  }

  useEffect(() => {
    if (selectedCity?.id) {
      setSkip(0);
      dispatch(resetRonsTipsPosts());
      dispatch(getRonsTipsCategoryPosts({ cityId: selectedCity?.id }));
    }
  }, [selectedCity?.id]);

  useEffect(() => {
    if (userData?.user_id) dispatch(getFollowedCategoriesAndSubcategories());
  }, []);

  const isMobile = useMobileDetect();

  return (
    <PageContainer showFooter={posts !== null && !loading}>
      <ContainerWithAd
        ad={
          <GoogleAdBannerVertical2
            channel={GOOGLE_ADS_RONS_TIPS_CHANNEL}
            keyValues={ronsTipsHomeGoogleAdsKeyValues}
          />
        }
      >
        {isMobile ? (
          <div className='mt-[16px] flex justify-center'>
            <GoogleAdBannerHorizontal
              id='top'
              channel={GOOGLE_ADS_RONS_TIPS_CHANNEL}
              keyValues={ronsTipsHomeGoogleAdsKeyValues}
              pos={1}
            />
          </div>
        ) : (
          <GoogleAdBannerTop
            keyValues={ronsTipsHomeGoogleAdsKeyValues}
            channel={GOOGLE_ADS_RONS_TIPS_CHANNEL}
          />
        )}
        {posts !== null && !loading ? (
          <InfiniteScroll
            style={{
              overflow: 'hidden',
            }}
            dataLength={Object.keys(posts).length || 0}
            next={loadMore}
            hasMore={skip < 6}
            scrollThreshold={0.8}
          >
            <PostsContainer data={posts} />
          </InfiniteScroll>
        ) : (
          // <div className='my-[24px] flex flex-col gap-6'>
          //   {Array.from({ length: 6 }).map((_, index) => (
          //     <RonsTipsCategorySectionSkeleton key={index} />
          //   ))}
          // </div>
          <></>
        )}
      </ContainerWithAd>
    </PageContainer>
  );
}

const PostsContainer = ({ data = {} }) => {
  const { GOOGLE_ADS_RONS_TIPS_CHANNEL, ronsTipsHomeGoogleAdsKeyValues } =
    useAds();
  const { ronsTipsCategoryIds, ronsTipsGuidesSubCategoryIds } =
    useRonsTipsUtils();
  const lang = useSelector(({ common }) => common?.language);
  const router = useRouter();
  const { city, tag_filter = 'most_popular' } = router.query;
  const [marketEvents, setMarketEvents] = useState(null);
  const dispatch = useDispatch();
  const isMobile = useMobileDetect();
  const selectedCity = useSelector(({ cities }) => cities?.selectedCity);
  const ronsTipsHomeAds = useSelector(({ ads }) => {
    return ads.ronsTipsHomeAds;
  });

  useEffect(() => {
    dispatch(getRonTipsHomeAdsAsync({ cityId: selectedCity?.id }));
  }, [isMobile, selectedCity?.id]);

  useEffect(() => {
    dispatch(getMarketPostsAsync({ discussion_type: 'events' })).then((res) => {
      if (res?.meta?.requestStatus === 'fulfilled') {
        setMarketEvents(res?.payload);
      }
    });
  }, []);
  function getSubscriberInfo() {
    if (checkIsAuthenticated()) {
      return lang?.rons_tips?.subscriber_section_subtitle_registered_user;
    } else {
      return lang?.rons_tips?.subscriber_section_subtitle_guest_user;
    }
  }
  function getSubscriberInfoButtonText() {
    if (checkIsAuthenticated()) {
      return lang?.common?.subscribe;
    } else {
      return lang?.common?.sign_up;
    }
  }

  function getSubscriberInfoLink() {
    if (checkIsAuthenticated()) {
      return ACCOUNT_SETTINGS_NEWSLETTER_CITY(city);
    } else {
      return LOGIN_SIGNUP_ROUTE();
    }
  }

  function showNationalTips() {
    const germanCity = 3; // lang_id for german cities;
    const allowedCities = all_cities
      .filter((item) => item?.lang_id === germanCity)
      .map((item) => item?.id);

    const cityId = selectedCity?.id;

    return allowedCities.includes(cityId);
  }

  return (
    <>
      <TagSection />
      <div className='w-full bg-white'>
        <RonsTipsCategorySection
          data={data['whats_up']}
          title={lang?.rons_tips?.whats_up}
          link={`${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/events/214?city=${city}&tag_filter=${tag_filter}&sort_by=desc&view_mode=list&rons_admin_tips=1`}
          adData={ronsTipsHomeAds?.[0] ?? null}
        />

        {data['whats_up']?.posts?.length > 0 && (
          <div className='mb-[32px] mt-[0px] flex justify-center'>
            <GoogleAdBannerHorizontal
              id='adslot_horizontal_2'
              pos={2}
              keyValues={ronsTipsHomeGoogleAdsKeyValues}
              channel={GOOGLE_ADS_RONS_TIPS_CHANNEL}
            />
          </div>
        )}

        <RonsTipsCategorySection
          data={data['food_and_shopping']}
          title={lang?.rons_tips?.food_and_shopping}
          link={`/rons-tips/food-shopping?city=${city}&tag_filter=${tag_filter}`}
          categoryId={ronsTipsCategoryIds['food_and_shop']}
          adData={ronsTipsHomeAds?.[1] ?? null}
        />

        {checkIsAuthenticated() && (
          <SubscribeSection
            title={lang?.rons_tips?.subscriber_section_title}
            subtitle={getSubscriberInfo()}
            buttonText={getSubscriberInfoButtonText()}
            link={getSubscriberInfoLink()}
          />
        )}

        <div className='mb-[16px] md:mb-[32px]'>
          <EcommerceAds />
        </div>

        {marketEvents && marketEvents?.events?.length > 0 && (
          <div className='mt-[32px] md:mt-[0px]'>
            <EventSection data={marketEvents?.events} />
          </div>
        )}

        {data['Roncast']?.posts?.length > 0 && (
          <div className=' mb-[16px] flex justify-center md:mb-[32px]'>
            <GoogleAdBannerHorizontal
              id='adslot_horizontal_3'
              pos={3}
              keyValues={ronsTipsHomeGoogleAdsKeyValues}
              channel={GOOGLE_ADS_RONS_TIPS_CHANNEL}
            />
          </div>
        )}

        {data['Roncast']?.posts?.length > 0 ? (
          <RonCastsSection data={data['Roncast']} />
        ) : null}

        <RonsTipsCategorySection
          data={data['people']}
          title={lang?.rons_tips?.inspired_by_you}
          link={`/rons-tips/people?city=${city}&tag_filter=${tag_filter}`}
          categoryId={ronsTipsCategoryIds['people']}
          adData={!isMobile ? ronsTipsHomeAds?.[2] : null}
        />

        {data['CityLife']?.posts?.length > 0 && (
          <div className='mb-6 mt-[40px] flex justify-center md:mb-20'>
            <GoogleAdBannerHorizontal
              id='adslot_horizontal_4'
              pos={4}
              keyValues={ronsTipsHomeGoogleAdsKeyValues}
              channel={GOOGLE_ADS_RONS_TIPS_CHANNEL}
            />
          </div>
        )}

        <RonsTipsCategorySection
          data={data['CityLife']}
          title={lang?.rons_tips?.city_life}
          link={`/rons-tips/guides/city-life?city=${city}&tag_filter=${tag_filter}`}
          subcategoryId={ronsTipsGuidesSubCategoryIds['city_life']}
          adData={isMobile ? ronsTipsHomeAds?.[2] : ronsTipsHomeAds?.[3]}
        />

        <div className='mt-[16px] flex flex-col bg-white md:mt-[0px]'>
          {data?.community?.posts.length > 0 && (
            <>
              <SectionHeader
                title={lang?.community?.survey}
                link={`/community?city=${city}&tag_filter=${tag_filter}`}
              />
              <div className='mb-10 flex flex-wrap gap-2'>
                {data?.community?.posts.length > 0 &&
                  data?.community?.posts.map((item, index) => (
                    <SurveyCard key={index} data={item} />
                  ))}
              </div>

              <Link
                href={`${process.env.NEXT_PUBLIC_FRONTEND_URL}/community?city=${city}&tag_filter=${tag_filter}`}
              >
                <button className='btn-outlined mx-auto my-4 flex w-[150px] items-center  font-bold sm:hidden'>
                  <span className='mr-[8px] cursor-pointer text-[14px]'>
                    {lang?.common?.see_all}
                  </span>
                  <CustomIcon image={longArrayRight} />
                </button>
              </Link>
            </>
          )}
          {data['citywriters']?.posts?.length > 0 && (
            <div className='mb-[16px] mt-[0px] flex justify-center md:mb-[32px]'>
              <GoogleAdBannerHorizontal
                id='adslot_horizontal_5'
                pos={5}
                keyValues={ronsTipsHomeGoogleAdsKeyValues}
                channel={GOOGLE_ADS_RONS_TIPS_CHANNEL}
              />
            </div>
          )}

          <RonsTipsCategorySection
            data={data['citywriters']}
            title={lang?.rons_tips?.city_writers}
            link={`/rons-tips/citywriters?city=${city}&tag_filter=${tag_filter}`}
            adData={isMobile ? ronsTipsHomeAds?.[3] : ronsTipsHomeAds?.[4]}
          />

          <div className='mb-[16px] mt-[40px] md:mb-[32px]'>
            <DonationBox />
          </div>

          <div className='mb-[16px] md:mb-[32px]'>
            <EcommerceAds apiCallAlreadyInProgress />
          </div>

          {showNationalTips() && (
            <RonsTipsCategorySection
              data={data['national']}
              title={lang?.rons_tips?.national_tips}
              link={`/rons-tips/national?city=${city}&tag_filter=${tag_filter}`}
              adData={isMobile ? ronsTipsHomeAds?.[4] : ronsTipsHomeAds?.[5]}
            />
          )}
        </div>

        {checkIsAuthenticated() && (
          <div className='mt-[16px] md:mt-[0px]'>
            <ForYouSection isHomePage />
          </div>
        )}
      </div>
    </>
  );
};
